import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';

import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';

interface UpdateStepStatusModalProps {
  open: boolean;
  stepTitle: string;
  currentStatus: string;
  newStatus: string;
  onConfirm: (note: string) => Promise<void>;
  onCancel: () => void;
}

const stepStatusLabel = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  under_review: 'Under Review',
  rejected: 'Rejected',
  failed: 'Failed',
  skipped: 'Skipped',
};

const UpdateStepStatusModal = ({
  open,
  stepTitle,
  currentStatus,
  newStatus,
  onConfirm,
  onCancel,
}: UpdateStepStatusModalProps) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(note);
      setNote(''); // Reset note after successful submission
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNote(''); // Reset note on cancel
    onCancel();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleCancel}>
      <DialogTitle>Update Step Status</DialogTitle>
      <DialogContent>
        <Stack vertical gap={16} style={{ paddingTop: '8px' }}>
          <Body>
            Changing status for <strong>{stepTitle}</strong> from{' '}
            <strong>{stepStatusLabel[currentStatus]}</strong> to{' '}
            <strong>{stepStatusLabel[newStatus]}</strong>
          </Body>
          <TextField
            fullWidth
            multiline
            label="Add notes (optional)"
            placeholder="Add any notes or comments about this status change..."
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={16} />}
          variant="contained"
          onClick={handleConfirm}
        >
          {isSubmitting ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateStepStatusModal;
